import React, { useEffect, useState, useRef, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import { get, inRange } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import {
  UsurpAdsConfig,
  MINIMUM_CARDS,
} from 'site-modules/shared/components/inventory/usurp-card-list/usurp-card-list-ad-units-data';
import { Storage } from 'site-modules/shared/utils/storage';
import { RECENTLY_VIEWED_STORAGE } from 'site-modules/shared/components/inventory/constants/recently-viewed-vins';
import { InventoryAdTargetingPrefetch } from 'site-modules/shared/components/native-ad/inventory-ad-targeting/inventory-ad-targeting-prefetch';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { Skeleton } from 'site-modules/shared/components/skeleton/skeleton';

const SLICE_INDICES = {
  true: 7,
  false: 8,
};

const NUM_USER_SSR_CARDS = 6;

function isLazyLoadImg({ index }) {
  return !inRange(index, 0, 1);
}

export const UsurpCardList = memo(
  ({
    inventory = [],
    cardComponent: CardComponent,
    cardKeyPrefix,
    isMobile = false,
    inventoryCount = null,
    isCrawler = false,
    disableAds = false,
    trackingParent = 'edm-entry-vehicle-card-list',
    trackingCreativeId = undefined,
    ...rest
  }) => {
    const [recentlyViewedVins, setRecentlyViewedVins] = useState([]);
    const localStorage = useRef(new Storage('localStorage'));

    useEffect(() => {
      const vins = localStorage.current.get(RECENTLY_VIEWED_STORAGE.PATH) || [];
      if (vins && vins.length) {
        setRecentlyViewedVins(vins);
      }
    }, []);

    const renderCard = useCallback(
      (vehicle, index) => {
        const card = (
          <CardComponent
            lazyLoadImage={isLazyLoadImg({ index })}
            vehicle={vehicle}
            importance={index === 0 ? 'high' : null}
            isViewed={recentlyViewedVins.includes(get(vehicle, 'vin'))}
            trackingCreativeId={trackingCreativeId}
            {...rest}
          />
        );
        return (
          <Col
            tag="li"
            xs={12}
            md={6}
            key={`${cardKeyPrefix ? `${cardKeyPrefix}_` : ''}${vehicle.vin}`}
            className="d-flex mb-0_75 mb-md-1_5"
          >
            {isCrawler || index < NUM_USER_SSR_CARDS ? (
              card
            ) : (
              <ClientSideRender
                placeholder={<Skeleton style={{ minHeight: 557 }} className="d-block w-100 rounded-12" />}
              >
                {card}
              </ClientSideRender>
            )}
          </Col>
        );
      },
      [cardKeyPrefix, recentlyViewedVins, rest, isCrawler, trackingCreativeId]
    );

    const renderCardListWithAds = useCallback(() => {
      if (disableAds) return inventory.map(renderCard);

      const cardListWithAds = [];
      const startIndex = SLICE_INDICES[isMobile];
      inventory.forEach((vehicle, index) => {
        cardListWithAds.push(renderCard(vehicle, index));
        if (UsurpAdsConfig[index + 1]) {
          cardListWithAds.push(UsurpAdsConfig[index + 1].component(InventoryAdTargetingPrefetch));
        }
      });

      if (inventory.length < MINIMUM_CARDS) {
        cardListWithAds.push(UsurpAdsConfig.default.component(InventoryAdTargetingPrefetch));
      } else if (UsurpAdsConfig.last) {
        cardListWithAds.push(UsurpAdsConfig.last.component(InventoryAdTargetingPrefetch));
      }

      if (startIndex && UsurpAdsConfig.exco && inventoryCount) {
        cardListWithAds.splice(startIndex, 0, UsurpAdsConfig.exco.component(InventoryAdTargetingPrefetch));
      }

      return cardListWithAds;
    }, [disableAds, isMobile, inventory, inventoryCount, renderCard]);

    return (
      <Row
        tag="ul"
        {...(trackingParent ? { 'data-tracking-parent': trackingParent } : {})}
        className="usurp-card-list list-unstyled align-items-stretch"
      >
        {renderCardListWithAds()}
      </Row>
    );
  }
);

UsurpCardList.displayName = 'UsurpCardList';

UsurpCardList.propTypes = {
  inventory: InventoryEntities.InventoriesVin,
  cardKeyPrefix: PropTypes.string,
  cardComponent: PropTypes.elementType.isRequired,
  isMobile: PropTypes.bool,
  inventoryCount: PropTypes.number,
  isCrawler: PropTypes.bool,
  disableAds: PropTypes.bool,
  trackingParent: PropTypes.string,
  trackingCreativeId: PropTypes.string,
};
